/**
 * Styled replies (removed 2026-10) let an agent embed `<artifact …>…</artifact>`
 * HTML blocks (tables, grids, callouts, small scripted widgets) in its
 * Markdown reply. Nothing writes them any more, but stored messages still hold
 * them.
 *
 * Every surface shows such a block as Markdown text: its title in bold, then
 * the text of its body. Scripts, styles, SVG and form controls are dropped and
 * every other tag is removed, so the HTML never runs. Same rules as the
 * dashboard's and the server's `legacyArtifacts.ts`, plus one widget-only
 * step: `<` and `>` that come out of decoded entities are escaped again, so
 * text like `&lt;img onerror=…&gt;` stays text in previews that read HTML.
 */

const ARTIFACT_BLOCK_RE = /<artifact\b([^>]*)>([\s\S]*?)<\/artifact\s*>/gi;
// An unclosed `<artifact …>` (or a stray close tag) left after the blocks are
// flattened.
const ARTIFACT_TAG_RE = /<\/?artifact\b[^>]*>/gi;

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

const decodeEntities = (text: string): string =>
  text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === "#") {
      const point =
        code[1] === "x" || code[1] === "X"
          ? parseInt(code.slice(2), 16)
          : parseInt(code.slice(1), 10);
      return Number.isFinite(point) && point > 0 && point <= 0x10ffff
        ? String.fromCodePoint(point)
        : match;
    }
    return ENTITIES[code.toLowerCase()] ?? match;
  });

/** Decoded text with any angle brackets escaped, so it can never become a tag. */
const toSafeText = (text: string): string =>
  decodeEntities(text).replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Text of an artifact body, keeping emphasis, headings, list bullets and line structure. */
const artifactBodyToText = (html: string): string =>
  toSafeText(
    html
      .replace(
        /<(script|style|svg|canvas|template|noscript|head|select|textarea|button)\b[\s\S]*?<\/\1\s*>/gi,
        " ",
      )
      .replace(/<!--[\s\S]*?-->/g, " ")
      .replace(/<input\b[^>]*>/gi, " ")
      .replace(
        /<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]\s*>/gi,
        (_m, inner: string) => `\n**${inner.trim()}**\n`,
      )
      .replace(
        /<(strong|b)\b[^>]*>([\s\S]*?)<\/\1\s*>/gi,
        (_m, _tag, inner: string) => `**${inner.trim()}**`,
      )
      .replace(
        /<(em|i)\b[^>]*>([\s\S]*?)<\/\1\s*>/gi,
        (_m, _tag, inner: string) => `*${inner.trim()}*`,
      )
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/t[dh]\s*>/gi, " | ")
      .replace(/<li\b[^>]*>/gi, "\n- ")
      .replace(
        /<\/(p|div|tr|li|ul|ol|table|blockquote|section|header|footer|h[1-6])\s*>/gi,
        "\n",
      )
      .replace(/<[^>]*>/g, ""),
  )
    .replace(/ \|\s*\n/g, "\n")
    .replace(/ \|\s*$/g, "")
    .replace(/[ \t]+/g, " ")
    // HTML source indentation would otherwise read as a Markdown code block.
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const readTitle = (attrs: string): string | null => {
  const match =
    /\btitle\s*=\s*"([^"]*)"/i.exec(attrs) ??
    /\btitle\s*=\s*'([^']*)'/i.exec(attrs);
  const title = match
    ? toSafeText(match[1].replace(/<[^>]*>/g, "")).trim()
    : "";
  return title || null;
};

export const hasLegacyArtifact = (content?: string | null): boolean =>
  !!content && /<artifact\b/i.test(content);

/** Replaces every `<artifact>` block with Markdown text; content without one is returned as is. */
export const flattenLegacyArtifacts = (content?: string | null): string => {
  if (!content) return "";
  if (!hasLegacyArtifact(content)) return content;
  return content
    .replace(ARTIFACT_BLOCK_RE, (_match, attrs: string, body: string) => {
      const title = readTitle(attrs);
      const text = artifactBodyToText(body);
      const block = [title ? `**${title}**` : "", text]
        .filter(Boolean)
        .join("\n\n");
      return block ? `\n\n${block}\n\n` : "\n\n";
    })
    .replace(ARTIFACT_TAG_RE, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
